import { apolloClient } from './graphql_client';
import { gql } from '@apollo/client';
import {
  AuthUserData,
  clearPendingEmailOtp,
  getPendingEmailOtp,
  persistAuthSession,
} from './wordpress_auth';
import { getAuthUserString, isAuthPersisted, setAuthUserString, clearAuthUser } from './auth_storage';
import { getRememberMePreference } from './remember_me';

interface LoginRole {
  name: string;
}

interface LoginUser {
  id: string;
  name: string;
  email: string;
  roles: {
    nodes: LoginRole[];
  };
}

export type AuthStepResult =
  | { status: 'authenticated'; authData: AuthUserData }
  | { status: 'totp_required'; challengeToken: string }
  | { status: 'totp_setup_required'; challengeToken: string };

const PENDING_TOTP_KEY = 'ngk_pending_totp';

export const storePendingTotp = (payload: {
  challengeToken: string;
  mode: 'verify' | 'setup';
  username?: string;
  password?: string;
}): void => {
  try {
    sessionStorage.setItem(PENDING_TOTP_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

export const getPendingTotp = (): {
  challengeToken: string;
  mode: 'verify' | 'setup';
  username?: string;
  password?: string;
} | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_TOTP_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearPendingTotp = (): void => {
  try {
    sessionStorage.removeItem(PENDING_TOTP_KEY);
  } catch {
    // ignore
  }
};

const extractError = (error: any, fallback: string): string => {
  if (error?.graphQLErrors?.[0]?.message) return error.graphQLErrors[0].message;
  if (error?.networkError?.message) return error.networkError.message;
  if (error?.message) return error.message;
  return fallback;
};

type LoginPayload = {
  requiresEmailOtp?: boolean | null;
  requiresTotp?: boolean | null;
  requiresTotpSetup?: boolean | null;
  challengeToken?: string | null;
  authToken?: string | null;
  refreshToken?: string | null;
  deviceTrustToken?: string | null;
  deviceTrustExpiresIn?: number | null;
  user?: LoginUser | null;
};

const resolveAuthStep = async (
  payload: LoginPayload | null | undefined,
  credentials?: { username: string; password: string } | null
): Promise<AuthStepResult> => {
  if (!payload) {
    throw new Error('Invalid verification response.');
  }

  if (payload.deviceTrustToken) {
    const { storeEmailOtpTrust } = await import('./email_otp_trust');
    storeEmailOtpTrust(payload.deviceTrustToken, payload.deviceTrustExpiresIn);
  }

  if (payload.requiresTotp) {
    if (!payload.challengeToken) {
      throw new Error('Authenticator challenge missing. Please sign in again.');
    }
    return { status: 'totp_required', challengeToken: payload.challengeToken };
  }

  if (payload.requiresTotpSetup) {
    if (!payload.challengeToken) {
      throw new Error('Authenticator setup challenge missing. Please sign in again.');
    }
    return { status: 'totp_setup_required', challengeToken: payload.challengeToken };
  }

  if (!payload.authToken) {
    throw new Error('Invalid verification response.');
  }

  let user = payload.user;
  // WPGraphQL may return user:null on login payloads (privacy). Recover via viewer.
  if (!user) {
    user = await fetchLoginUserWithToken(payload.authToken);
  }
  if (!user) {
    const pending = getPendingEmailOtp();
    const email = credentials?.username || pending?.username || '';
    user = {
      id: '0',
      name: email || 'User',
      email,
      roles: { nodes: [] },
    };
  }

  const pending = getPendingEmailOtp();
  const authData = await persistAuthSession(
    payload.authToken,
    user,
    payload.refreshToken,
    credentials ||
      (pending
        ? { username: pending.username, password: pending.password }
        : null)
  );
  clearPendingEmailOtp();
  clearPendingTotp();
  return { status: 'authenticated', authData };
};

const GET_VIEWER_FOR_LOGIN = gql`
  query ViewerForLogin {
    viewer {
      id
      name
      email
      roles {
        nodes {
          name
        }
      }
    }
  }
`;

async function fetchLoginUserWithToken(authToken: string): Promise<LoginUser | null> {
  try {
    const persist = isAuthPersisted() || getRememberMePreference();
    const previous = getAuthUserString();
    setAuthUserString(
      JSON.stringify({ accessToken: authToken, user: {}, roles: [] }),
      persist
    );
    const { data } = await apolloClient.query<{
      viewer?: LoginUser | null;
    }>({
      query: GET_VIEWER_FOR_LOGIN,
      fetchPolicy: 'network-only',
    });
    if (previous) {
      setAuthUserString(previous, persist);
    } else {
      clearAuthUser();
    }
    return data?.viewer || null;
  } catch {
    try {
      clearAuthUser();
    } catch {
      // ignore
    }
    return null;
  }
}

const VERIFY_EMAIL_OTP = gql`
  mutation VerifyEmailOtp($challengeToken: String!, $code: String!) {
    verifyEmailOtp(input: { challengeToken: $challengeToken, code: $code }) {
      requiresEmailOtp
      requiresTotp
      requiresTotpSetup
      challengeToken
      emailHint
      expiresIn
      authToken
      refreshToken
      deviceTrustToken
      deviceTrustExpiresIn
      user {
        id
        name
        email
        roles {
          nodes {
            name
          }
        }
      }
    }
  }
`;

const VERIFY_TOTP = gql`
  mutation VerifyTotp($challengeToken: String!, $code: String!) {
    verifyTotp(input: { challengeToken: $challengeToken, code: $code }) {
      requiresEmailOtp
      requiresTotp
      requiresTotpSetup
      challengeToken
      authToken
      refreshToken
      user {
        id
        name
        email
        roles {
          nodes {
            name
          }
        }
      }
    }
  }
`;

const BEGIN_TOTP = gql`
  mutation BeginTotpEnrollment($challengeToken: String) {
    beginTotpEnrollment(input: { challengeToken: $challengeToken }) {
      otpauthUrl
      secret
      recoveryCodes
    }
  }
`;

const CONFIRM_TOTP = gql`
  mutation ConfirmTotpEnrollment($challengeToken: String, $code: String!) {
    confirmTotpEnrollment(input: { challengeToken: $challengeToken, code: $code }) {
      requiresEmailOtp
      requiresTotp
      requiresTotpSetup
      challengeToken
      authToken
      refreshToken
      user {
        id
        name
        email
        roles {
          nodes {
            name
          }
        }
      }
    }
  }
`;

const RESET_USER_TOTP = gql`
  mutation ResetUserTotp($userId: ID!) {
    resetUserTotp(input: { userId: $userId }) {
      success
    }
  }
`;

const DISABLE_OWN_TOTP = gql`
  mutation DisableOwnTotp($code: String!) {
    disableOwnTotp(input: { code: $code }) {
      success
      emailOtpEnabled
      totpEnabled
    }
  }
`;

const SET_OWN_EMAIL_OTP = gql`
  mutation SetOwnEmailOtpPreference($enabled: Boolean!) {
    setOwnEmailOtpPreference(input: { enabled: $enabled }) {
      success
      emailOtpEnabled
      totpEnabled
    }
  }
`;

const GET_VIEWER_MFA = gql`
  query ViewerMfaStatus {
    viewer {
      id
      databaseId
      totpEnabled
      emailOtpEnabled
    }
  }
`;

export type ViewerMfaStatus = {
  totpEnabled: boolean;
  emailOtpEnabled: boolean;
};

export const verifyEmailOtpStep = async (
  challengeToken: string,
  code: string
): Promise<AuthStepResult> => {
  try {
    const { data } = await apolloClient.mutate<{ verifyEmailOtp?: LoginPayload | null }>({
      mutation: VERIFY_EMAIL_OTP,
      variables: {
        challengeToken,
        code: code.replace(/\D/g, ''),
      },
    });
    return resolveAuthStep(data?.verifyEmailOtp);
  } catch (error: any) {
    throw new Error(extractError(error, 'Invalid or expired verification code.'));
  }
};

export const verifyTotp = async (
  challengeToken: string,
  code: string
): Promise<AuthUserData> => {
  try {
    const pending = getPendingTotp();
    const { data } = await apolloClient.mutate<{ verifyTotp?: LoginPayload | null }>({
      mutation: VERIFY_TOTP,
      variables: { challengeToken, code },
    });
    const result = await resolveAuthStep(
      data?.verifyTotp,
      pending?.username && pending?.password
        ? { username: pending.username, password: pending.password }
        : null
    );
    if (result.status !== 'authenticated') {
      throw new Error('Authenticator verification incomplete.');
    }
    return result.authData;
  } catch (error: any) {
    throw new Error(extractError(error, 'Invalid authenticator or recovery code.'));
  }
};

export const beginTotpEnrollment = async (
  challengeToken?: string | null
): Promise<{ otpauthUrl: string; secret: string; recoveryCodes: string[] }> => {
  try {
    const { data } = await apolloClient.mutate<{
      beginTotpEnrollment?: {
        otpauthUrl?: string | null;
        secret?: string | null;
        recoveryCodes?: string[] | null;
      } | null;
    }>({
      mutation: BEGIN_TOTP,
      variables: { challengeToken: challengeToken || null },
    });
    const payload = data?.beginTotpEnrollment;
    if (!payload?.otpauthUrl || !payload.secret) {
      throw new Error('Unable to start authenticator setup.');
    }
    return {
      otpauthUrl: payload.otpauthUrl,
      secret: payload.secret,
      recoveryCodes: payload.recoveryCodes || [],
    };
  } catch (error: any) {
    throw new Error(extractError(error, 'Unable to start authenticator setup.'));
  }
};

export const confirmTotpEnrollment = async (
  code: string,
  challengeToken?: string | null
): Promise<AuthStepResult | { status: 'setup_complete' }> => {
  try {
    const pending = getPendingTotp();
    const emailPending = getPendingEmailOtp();
    const { data } = await apolloClient.mutate<{ confirmTotpEnrollment?: LoginPayload | null }>({
      mutation: CONFIRM_TOTP,
      variables: {
        code,
        challengeToken: challengeToken || null,
      },
    });

    const payload = data?.confirmTotpEnrollment;
    if (payload?.authToken && payload.user) {
      return resolveAuthStep(
        payload,
        pending?.username && pending?.password
          ? { username: pending.username, password: pending.password }
          : emailPending
            ? { username: emailPending.username, password: emailPending.password }
            : null
      );
    }

    if (!challengeToken) {
      clearPendingTotp();
      return { status: 'setup_complete' };
    }

    throw new Error('Unable to confirm authenticator setup.');
  } catch (error: any) {
    throw new Error(extractError(error, 'Unable to confirm authenticator setup.'));
  }
};

export const resetUserTotp = async (userId: string): Promise<void> => {
  try {
    const { data } = await apolloClient.mutate<{
      resetUserTotp?: { success?: boolean | null } | null;
    }>({
      mutation: RESET_USER_TOTP,
      variables: { userId },
    });
    if (!data?.resetUserTotp?.success) {
      throw new Error('Unable to reset authenticator 2FA.');
    }
  } catch (error: any) {
    throw new Error(extractError(error, 'Unable to reset authenticator 2FA.'));
  }
};

export const disableOwnTotp = async (code: string): Promise<ViewerMfaStatus> => {
  try {
    const { data } = await apolloClient.mutate<{
      disableOwnTotp?: {
        success?: boolean | null;
        emailOtpEnabled?: boolean | null;
        totpEnabled?: boolean | null;
      } | null;
    }>({
      mutation: DISABLE_OWN_TOTP,
      variables: { code },
    });
    if (!data?.disableOwnTotp?.success) {
      throw new Error('Unable to turn off authenticator 2FA.');
    }
    // Force Email OTP on next login (server also clears trusts).
    try {
      const { clearEmailOtpTrust } = await import('./email_otp_trust');
      clearEmailOtpTrust();
    } catch {
      // ignore
    }
    return {
      totpEnabled: !!data.disableOwnTotp.totpEnabled,
      emailOtpEnabled: !!data.disableOwnTotp.emailOtpEnabled,
    };
  } catch (error: any) {
    throw new Error(extractError(error, 'Unable to turn off authenticator 2FA.'));
  }
};

export const setOwnEmailOtpPreference = async (
  enabled: boolean
): Promise<ViewerMfaStatus> => {
  try {
    const { data } = await apolloClient.mutate<{
      setOwnEmailOtpPreference?: {
        success?: boolean | null;
        emailOtpEnabled?: boolean | null;
        totpEnabled?: boolean | null;
      } | null;
    }>({
      mutation: SET_OWN_EMAIL_OTP,
      variables: { enabled },
    });
    if (!data?.setOwnEmailOtpPreference?.success) {
      throw new Error('Unable to update Email OTP preference.');
    }
    return {
      totpEnabled: !!data.setOwnEmailOtpPreference.totpEnabled,
      emailOtpEnabled: !!data.setOwnEmailOtpPreference.emailOtpEnabled,
    };
  } catch (error: any) {
    throw new Error(extractError(error, 'Unable to update Email OTP preference.'));
  }
};

export const fetchViewerMfaStatus = async (): Promise<ViewerMfaStatus> => {
  try {
    const { data } = await apolloClient.query<{
      viewer?: {
        totpEnabled?: boolean | null;
        emailOtpEnabled?: boolean | null;
      } | null;
    }>({
      query: GET_VIEWER_MFA,
      fetchPolicy: 'network-only',
    });
    return {
      totpEnabled: !!data?.viewer?.totpEnabled,
      emailOtpEnabled: data?.viewer?.emailOtpEnabled !== false,
    };
  } catch {
    return { totpEnabled: false, emailOtpEnabled: true };
  }
};

/** @deprecated Prefer fetchViewerMfaStatus */
export const fetchViewerTotpEnabled = async (): Promise<boolean> => {
  const status = await fetchViewerMfaStatus();
  return status.totpEnabled;
};
